import './App.css';
import React, {useEffect, useRef} from 'react';
import {
    BrowserRouter as Router,
    Routes,
    Route,
    Navigate,
    useLocation
} from 'react-router-dom';

import Launcher from './Launcher';
import PoemEditor from './PoemEditor';
import TaggingPage from './components/TaggingPage';
import PoemList from './components/PoemList';
import AnnotationPage from './components/AnnotationPage';
import Tutorial from './components/Tutorial';
import ParticipantGate from "./ParticipantGate";
import CreatorRegistration from "./components/CreatorRegistration";
import EvaluatorRegistration from "./components/EvaluatorRegistration";
import ScenarioFeedbackPage from "./components/ScenarioFeedbackPage";
import ScreeningPage from './components/ScreeningPage';
import ConsentPage from './components/ConsentPage';

const logUserSession = async (pid, scenario) => {
    try {
        await fetch('/api/log/session', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ pid, scenario })
        });
    } catch (error) {
        console.error('Error logging user session:', error);
    }
};

function InnerApp() {
    const { search } = useLocation();
    const params = new URLSearchParams(search);
    const mode = params.get('interface') || params.get('mode');
    const ENABLE_WRITE = process.env.REACT_APP_ENABLE_WRITE !== 'false';
    const ENABLE_ANNOTATE = process.env.REACT_APP_ENABLE_ANNOTATE === 'true';
    const blocked =
        (mode === 'annotate' && !ENABLE_ANNOTATE) ||
        (mode === 'write' && !ENABLE_WRITE);

    const pid = params.get('pid');  // participant ID
    const scenario = params.get('scenario'); // specific scenario for write mode

    const hasSent = useRef(false);
    useEffect(() => {
        if (pid && mode) {
            if (hasSent.current){
                return;
            }
            hasSent.current = true;
            const existing = localStorage.getItem('session_id');
            if (existing){
                return;
            }
            let sessionScenario = mode;
            if (mode === 'write' && scenario) {
                sessionScenario = scenario;
            }
            logUserSession(pid, sessionScenario);
        }
    }, [pid, mode, scenario]);
    if (blocked) {
        return <Navigate to="/" replace />;
    }

    // 1) mode is not defined so the launcher is shown
    if (!mode) {
        return <Launcher />;
    }

    // 2) Write mode – editor & tagging interfaces
    if (mode === 'write') {
        return (
            <div className="container">
                <Routes>
                    <Route path="/tutorial" element={<Tutorial />} />
                    <Route path="/write-poem" element={<PoemEditor />} />
                    <Route path="/tag-poem" element={<TaggingPage />} />
                    <Route path="/start" element={<ParticipantGate />} />
                    <Route path="/screening" element={<ScreeningPage />} />
                    <Route path="/consent" element={<ConsentPage />} />
                    <Route path="/register-creator" element={<CreatorRegistration />} />
                    <Route path="/feedback/:scenario" element={<ScenarioFeedbackPage />} />
                    {/* Fallback to editor */}
                    <Route
                        path="*"
                        element={<Navigate to={`/write-poem${search}`} replace />}
                    />
                </Routes>
            </div>
        );
    }

    // 3) Annotate mode – list & annotation
    if (mode === 'annotate') {
        return (
            <div className="container">
                {/*<Menu mode="annotate" />*/}
                <Routes>
                    <Route
                        path="/"
                        element={<Navigate to={`/poems${search}`} replace />}
                    />
                    <Route path="/tutorial" element={<Tutorial />} />
                    <Route path="/poems" element={<PoemList />} />
                    <Route path="/start" element={<ParticipantGate />} />
                    <Route path="/annotation/:poemId" element={<AnnotationPage />} />
                    <Route path="/screening" element={<ScreeningPage />} />
                    <Route path="/consent" element={<ConsentPage />} />
                    <Route path="/register-evaluator" element={<EvaluatorRegistration />} />
                    <Route path="/feedback/:scenario" element={<ScenarioFeedbackPage />} />
                    {/* Fallback to list */}
                    <Route
                        path="*"
                        element={<Navigate to={`/poems${search}`} replace />}
                    />
                </Routes>
            </div>
        );
    }

    // 4) Invalid mode
    return <div>Invalid mode</div>;
}

export default function App() {
    return (
        <Router>
            <InnerApp />
        </Router>
    );
}
